import React, { useRef } from 'react';
import moment from 'moment';
import { Meal } from '../../types';
import { MEAL_CATEGORIES } from '../../utils/constants';

const CATEGORY_COLORS: Record<string, { border: string; bg: string; text: string }> = {
  'Repas rapide':      { border: '#0097a7', bg: '#e0f7fa', text: '#006064' },
  'Dîner familial':    { border: '#f9a825', bg: '#fffde7', text: '#e65100' },
  'Occasion spéciale': { border: '#e91e63', bg: '#fce4ec', text: '#880e4f' },
  'Restes':            { border: '#558b2f', bg: '#f1f8e9', text: '#33691e' },
  'Restaurant':        { border: '#e64a19', bg: '#fbe9e7', text: '#bf360c' },
};

const DEFAULT_COLORS = { border: '#607D8B', bg: '#f5f5f5', text: '#37474f' };

const getCategoryEmoji = (category: string) =>
  MEAL_CATEGORIES.find(c => c.name === category)?.emoji ?? '🍽️';

interface WeekViewProps {
  weekStartStr: string;
  meals: Meal[];
  onAddMeal: (dateString: string) => void;
  onEditMeal: (meal: Meal) => void;
  onMoveMeal: (meal: Meal, newDateString: string) => void;
}

// ── Pointer-based drag state ─────────────────────────────────────────────────
interface DragState {
  meal: Meal;
  startX: number;
  startY: number;
  ghost: HTMLDivElement | null;
  active: boolean;
}

function spawnGhost(meal: Meal, x: number, y: number): HTMLDivElement {
  const colors = CATEGORY_COLORS[meal.category] ?? DEFAULT_COLORS;
  const el = document.createElement('div');
  el.style.cssText = [
    'position:fixed',
    'z-index:9999',
    `left:${x - 70}px`,
    `top:${y - 20}px`,
    `background:${colors.bg}`,
    `border-left:3px solid ${colors.border}`,
    `color:${colors.text}`,
    'padding:6px 12px',
    'border-radius:8px',
    'font-size:12px',
    'font-weight:600',
    'box-shadow:0 6px 20px rgba(0,0,0,0.18)',
    'pointer-events:none',
    'white-space:nowrap',
    'will-change:left,top',
    'user-select:none',
  ].join(';');
  el.textContent = `${getCategoryEmoji(meal.category)} ${meal.name}`;
  document.body.appendChild(el);
  return el;
}

function destroyGhost(ghost: HTMLDivElement | null) {
  if (ghost?.parentNode) ghost.parentNode.removeChild(ghost);
}

// ─────────────────────────────────────────────────────────────────────────────

const WeekView: React.FC<WeekViewProps> = ({ weekStartStr, meals, onAddMeal, onEditMeal, onMoveMeal }) => {
  const today = moment().format('YYYY-MM-DD');
  const drag = useRef<DragState | null>(null);
  const days = Array.from({ length: 7 }, (_, i) => moment(weekStartStr).add(i, 'days'));
  const getMealsForDay = (dateString: string) => meals.filter(m => m.dateString === dateString);

  // ── Unified pointer handlers (mouse + touch + stylus) ────────────────────

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>, meal: Meal) => {
    if (e.button !== 0) return; // primary button / first touch only
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { meal, startX: e.clientX, startY: e.clientY, ghost: null, active: false };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>, meal: Meal) => {
    const d = drag.current;
    if (!d || d.meal._id !== meal._id) return;

    const dist = Math.hypot(e.clientX - d.startX, e.clientY - d.startY);

    if (!d.active && dist > 8) {
      d.active = true;
      d.ghost = spawnGhost(meal, e.clientX, e.clientY);
      e.currentTarget.style.opacity = '0.45';
    }

    if (d.active && d.ghost) {
      d.ghost.style.left = `${e.clientX - 70}px`;
      d.ghost.style.top  = `${e.clientY - 20}px`;
    }
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>, meal: Meal) => {
    const d = drag.current;
    if (!d || d.meal._id !== meal._id) return;

    destroyGhost(d.ghost);
    e.currentTarget.style.opacity = '';

    if (d.active) {
      // elementFromPoint ignores pointer-events:none ghost → returns day container
      const target = document.elementFromPoint(e.clientX, e.clientY);
      const dayEl  = target?.closest('[data-date]') as HTMLElement | null;
      const toDate = dayEl?.dataset.date;
      if (toDate && toDate !== meal.dateString) {
        onMoveMeal(meal, toDate);
      }
    }

    drag.current = null;
  };

  const onPointerCancel = (e: React.PointerEvent<HTMLButtonElement>, meal: Meal) => {
    const d = drag.current;
    if (!d || d.meal._id !== meal._id) return;
    destroyGhost(d.ghost);
    e.currentTarget.style.opacity = '';
    drag.current = null;
  };

  // Shared style/props for every draggable meal card
  const mealCardProps = (meal: Meal) => {
    const colors = CATEGORY_COLORS[meal.category] ?? DEFAULT_COLORS;
    return {
      type: 'button' as const,
      onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => onPointerDown(e, meal),
      onPointerMove: (e: React.PointerEvent<HTMLButtonElement>) => onPointerMove(e, meal),
      onPointerUp:   (e: React.PointerEvent<HTMLButtonElement>) => onPointerUp(e, meal),
      onPointerCancel: (e: React.PointerEvent<HTMLButtonElement>) => onPointerCancel(e, meal),
      onClick: () => {
        // Suppress click when the pointer was dragging
        if (!drag.current?.active) onEditMeal(meal);
      },
      style: {
        touchAction: 'none' as const,
        userSelect: 'none' as const,
        backgroundColor: colors.bg,
        borderLeftColor: colors.border,
        color: colors.text,
      },
    };
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Desktop: 7-column grid ── */}
      <div className="hidden md:grid grid-cols-7 gap-2">
        {days.map(day => {
          const dateString = day.format('YYYY-MM-DD');
          const isToday    = dateString === today;
          const isPast     = day.isBefore(moment(), 'day');
          const dayMeals   = getMealsForDay(dateString);

          return (
            <div
              key={dateString}
              data-date={dateString}
              className={`flex flex-col rounded-xl overflow-hidden min-h-[220px] transition-shadow ${
                isToday
                  ? 'ring-2 ring-primary/40 bg-primary/5'
                  : 'bg-white shadow-soft hover:shadow-medium'
              }`}
            >
              <div className={`px-2 py-2 text-center ${
                isToday
                  ? 'bg-primary text-white'
                  : 'bg-gray-50 text-gray-600 border-b border-gray-100'
              }`}>
                <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80">
                  {day.format('ddd')}
                </div>
                <div className="text-xl font-bold leading-tight mt-0.5">
                  {day.format('D')}
                </div>
              </div>

              <div className="flex-1 p-1.5 flex flex-col gap-1">
                {dayMeals.map(meal => (
                  <button
                    key={meal._id}
                    {...mealCardProps(meal)}
                    className="w-full text-left rounded-lg px-2 py-1.5 border-l-[3px] text-xs font-medium cursor-grab active:cursor-grabbing hover:opacity-80 transition-opacity"
                  >
                    <div className="font-semibold truncate leading-tight">
                      {getCategoryEmoji(meal.category)} {meal.name}
                    </div>
                    {meal.people && meal.people.length > 0 && (
                      <div className="flex flex-wrap gap-0.5 mt-0.5">
                        {meal.people.slice(0, 3).map(p => (
                          <span key={p} className="opacity-60 text-[9px]">{p}</span>
                        ))}
                      </div>
                    )}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => onAddMeal(dateString)}
                  className={`mt-auto w-full rounded-lg py-1.5 text-xs font-medium transition-colors ${
                    dayMeals.length === 0 && !isPast
                      ? 'border border-dashed border-gray-200 text-gray-400 hover:border-primary/40 hover:text-primary hover:bg-primary/5'
                      : 'text-gray-200 hover:text-gray-400 hover:bg-gray-50'
                  }`}
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Mobile: vertical list ── */}
      <div className="md:hidden flex flex-col gap-2">
        {days.map(day => {
          const dateString = day.format('YYYY-MM-DD');
          const isToday    = dateString === today;
          const isPast     = day.isBefore(moment(), 'day');
          const dayMeals   = getMealsForDay(dateString);

          return (
            <div
              key={dateString}
              data-date={dateString}
              className={`bg-white rounded-xl p-3 shadow-soft ${isToday ? 'ring-2 ring-primary/40' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-14 flex-shrink-0">
                  <div className={`text-sm font-semibold capitalize ${
                    isToday ? 'text-primary' : isPast ? 'text-gray-400' : 'text-gray-700'
                  }`}>
                    {day.format('ddd')}
                  </div>
                  <div className="text-xs text-gray-400">{day.format('D MMM')}</div>
                </div>

                <div className="flex-1 min-w-0">
                  {dayMeals.length > 0 ? (
                    <div className="flex flex-col gap-1.5">
                      {dayMeals.map(meal => (
                        <button
                          key={meal._id}
                          {...mealCardProps(meal)}
                          className="w-full text-left rounded-lg px-2 py-1.5 border-l-[3px] text-xs font-medium transition-opacity"
                        >
                          <div className="font-semibold truncate">
                            {getCategoryEmoji(meal.category)} {meal.name}
                          </div>
                          {meal.people && meal.people.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {meal.people.slice(0, 4).map(p => (
                                <span key={p} className="opacity-60 text-[9px]">{p}</span>
                              ))}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className={`text-sm ${isPast ? 'text-gray-300' : 'text-gray-400'}`}>
                      {isPast ? 'Aucun repas' : 'Rien de prévu'}
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => onAddMeal(dateString)}
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-gray-300 hover:text-primary hover:bg-primary/10 transition-colors text-xl font-light leading-none"
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default WeekView;
